import { useState, useEffect } from "react";
import {
  TrendingUp,
  Target,
  Clock,
  BookOpen,
  BarChart3,
  Calendar,
  Zap,
  Trophy,
  Activity,
  CheckCircle,
  Loader,
  RefreshCw,
  Eye,
  Star,
  Flame,
  AlertCircle,
} from "lucide-react";
import { apiService } from "../services/api";
import styles from "./Progress.module.css";

const Progress = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [recommendedAssessments, setRecommendedAssessments] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardResponse, recommendedResponse] = await Promise.allSettled(
        [
          apiService.progress.getDashboard(),
          apiService.progress.getRecommendedAssessments(6),
        ],
      );

      // Handle dashboard data
      if (dashboardResponse.status === "fulfilled" && dashboardResponse.value) {
        console.log("Dashboard data:", dashboardResponse.value);
        setDashboardData(dashboardResponse.value);
      } else {
        console.error("Dashboard error:", dashboardResponse.reason);
        setError("Failed to load progress data");
      }

      // Handle recommended assessments
      if (
        recommendedResponse.status === "fulfilled" &&
        recommendedResponse.value?.recommendations
      ) {
        setRecommendedAssessments(recommendedResponse.value.recommendations);
      }
    } catch (err) {
      console.error("Error fetching progress data:", err);
      setError("Failed to load progress data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchProgressData();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner":
        return "#10b981";
      case "intermediate":
        return "#f59e0b";
      case "advanced":
        return "#2563eb";
      case "expert":
        return "#7c3aed";
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className={`${styles.progressPage} ${styles.loading}`}>
        <div className={styles.loadingContainer}>
          <Loader size={48} className={styles.spinner} />
          <h2>Loading Your Progress...</h2>
          <p>Analyzing your learning journey</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.progressPage} ${styles.loading}`}>
        <div className={styles.loadingContainer}>
          <AlertCircle size={48} color="#ef4444" />
          <h2>Error Loading Progress</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={handleRefresh}>
            <RefreshCw size={18} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.assessmentStats || {
    totalAttempts: 0,
    passedAttempts: 0,
    averageScore: 0,
  };
  const learningStreak = dashboardData?.learningStreak || 0;
  const topSkills = dashboardData?.topSkills || [];
  const recentAssessments = dashboardData?.recentAssessments || [];

  return (
    <div className={styles.progressPage}>
      <div className={styles.progressContainer}>
        {/* Header */}
        <div className={styles.progressHeader}>
          <div className={styles.headerContent}>
            <h1>
              <TrendingUp size={32} />
              Your Progress
            </h1>
            <p>Track your learning journey and celebrate your achievements</p>
          </div>
          <div className={styles.headerActions}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className={styles.statsOverview}>
          <div className={`${styles.statCard} ${styles.blue}`}>
            <div className={styles.statIcon}>
              <BookOpen size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalAttempts}</div>
              <div className={styles.statLabel}>Total Attempts</div>
              <div className={styles.statChange}>
                <CheckCircle size={14} />
                {stats.passedAttempts} Completed
              </div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.green}`}>
            <div className={styles.statIcon}>
              <Target size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.averageScore}%</div>
              <div className={styles.statLabel}>Avg Score</div>
              <div className={styles.statChange}>
                <TrendingUp size={14} />
                Overall Performance
              </div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.purple}`}>
            <div className={styles.statIcon}>
              <Flame size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{learningStreak}</div>
              <div className={styles.statLabel}>Day Streak</div>
              <div className={styles.statChange}>
                <Zap size={14} />
                {learningStreak > 0 ? "Keep it up!" : "Start today!"}
              </div>
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.primary}`}>
            <div className={styles.statIcon}>
              <Star size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{topSkills.length}</div>
              <div className={styles.statLabel}>Skills Practiced</div>
              <div className={styles.statChange}>
                <Trophy size={14} />
                Keep Learning
              </div>
            </div>
          </div>
        </div>

        <div className={styles.progressContent}>
          {/* Skills Performance */}
          {topSkills.length > 0 && (
            <div className={styles.contentSection}>
              <div className={styles.sectionHeader}>
                <h2>
                  <BarChart3 size={20} />
                  Skills Performance
                </h2>
                <span className={styles.sectionBadge}>{topSkills.length}</span>
              </div>
              <div className={styles.skillsGrid}>
                {topSkills.map((skill, index) => (
                  <div key={index} className={styles.skillProgressCard}>
                    <div className={styles.skillHeader}>
                      <div
                        className={styles.skillIcon}
                        style={{
                          background: `${getScoreColor(skill.averageScore)}20`,
                          color: getScoreColor(skill.averageScore),
                        }}
                      >
                        <BookOpen size={20} />
                      </div>
                      <div className={styles.skillInfo}>
                        <h4>{skill.skillName}</h4>
                        <span className={styles.skillCategory}>
                          {skill.assessmentCount} assessment
                          {skill.assessmentCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <span
                        className={styles.levelBadge}
                        style={{
                          background: getScoreColor(skill.averageScore),
                        }}
                      >
                        {skill.averageScore >= 80
                          ? "Expert"
                          : skill.averageScore >= 60
                            ? "Intermediate"
                            : "Beginner"}
                      </span>
                    </div>
                    <div className={styles.skillProgressBar}>
                      <div className={styles.progressTrack}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${skill.averageScore}%`,
                            background: getScoreColor(skill.averageScore),
                          }}
                        ></div>
                      </div>
                      <span className={styles.progressPercentage}>
                        {skill.averageScore}%
                      </span>
                    </div>
                    <div className={styles.skillStats}>
                      <div className={styles.stat}>
                        <span className={styles.statLabel}>Attempts</span>
                        <span className={styles.statValue}>
                          {skill.assessmentCount}
                        </span>
                      </div>
                      <div className={styles.stat}>
                        <span className={styles.statLabel}>Best</span>
                        <span
                          className={styles.statValue}
                          style={{ color: getScoreColor(skill.highestScore) }}
                        >
                          {skill.highestScore}%
                        </span>
                      </div>
                      <div className={styles.stat}>
                        <span className={styles.statLabel}>Lowest</span>
                        <span className={styles.statValue}>
                          {skill.lowestScore}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Assessments */}
          {recommendedAssessments.length > 0 && (
            <div className={styles.contentSection}>
              <div className={styles.sectionHeader}>
                <h2>
                  <Zap size={20} />
                  Recommended For You
                </h2>
                <span className={styles.sectionBadge}>
                  {recommendedAssessments.length}
                </span>
              </div>
              <div className={styles.recommendationsList}>
                {recommendedAssessments.map((item, index) => {
                  const priority =
                    item.userAttemptCount === 0
                      ? "high"
                      : item.userBestScore < 50
                        ? "high"
                        : item.userBestScore < 70
                          ? "medium"
                          : "low";
                  const priorityClass =
                    priority === "high"
                      ? styles.priorityHigh
                      : priority === "medium"
                        ? styles.priorityMedium
                        : styles.priorityLow;

                  return (
                    <div
                      key={index}
                      className={`${styles.recommendationItem} ${priorityClass}`}
                    >
                      <div className={styles.priorityIndicator}></div>
                      <div className={styles.recommendationContent}>
                        <h4>{item.name}</h4>
                        <p>{item.description}</p>
                        <div className={styles.recommendationMeta}>
                          <span
                            className={styles.difficulty}
                            style={{
                              background: `${getDifficultyColor(item.difficulty)}20`,
                              color: getDifficultyColor(item.difficulty),
                            }}
                          >
                            {item.difficulty}
                          </span>
                          <span className={styles.time}>
                            <Clock size={12} />
                            {item.duration}
                          </span>
                          {item.skillName && (
                            <span className={styles.skillTag}>
                              {item.skillName}
                            </span>
                          )}
                          {item.userAttemptCount > 0 && (
                            <span className={styles.attemptBadge}>
                              {item.userAttemptCount} attempt
                              {item.userAttemptCount !== 1 ? "s" : ""} •{" "}
                              {item.userBestScore}% best
                            </span>
                          )}
                        </div>
                      </div>
                      <a
                        href={`/assessment/${item.id}`}
                        className={styles.takeAssessmentBtn}
                      >
                        <Eye size={16} />
                        {item.userAttemptCount === 0 ? "Start" : "Retry"}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentAssessments.length > 0 && (
            <div className={styles.contentSection}>
              <div className={styles.sectionHeader}>
                <h2>
                  <Activity size={20} />
                  Recent Activity
                </h2>
                <a href="/assessments" className={styles.viewAll}>
                  View All
                </a>
              </div>
              <div className={styles.assessmentHistoryList}>
                {recentAssessments.map((activity, index) => {
                  const scoreClass =
                    activity.percentage >= 80
                      ? styles.excellent
                      : activity.percentage >= 60
                        ? styles.good
                        : styles.needsImprovement;

                  return (
                    <div key={index} className={styles.assessmentHistoryItem}>
                      <div className={styles.assessmentInfo}>
                        <div className={styles.assessmentIcon}>
                          <BookOpen size={20} />
                        </div>
                        <div className={styles.assessmentDetails}>
                          <h4>{activity.title}</h4>
                          <div className={styles.assessmentMeta}>
                            {activity.skillName && (
                              <span className={styles.category}>
                                {activity.skillName}
                              </span>
                            )}
                            <span>
                              <Calendar size={12} />
                              {new Date(
                                activity.completedAt,
                              ).toLocaleDateString()}
                            </span>
                            <span>Attempt #{activity.attemptNumber}</span>
                          </div>
                        </div>
                      </div>
                      <div className={styles.assessmentScore}>
                        <div className={`${styles.scoreBadge} ${scoreClass}`}>
                          {activity.percentage}%
                        </div>
                      </div>
                      <div className={styles.assessmentActions}>
                        <a
                          href={`/assessment-report/${activity.id}`}
                          className="btn btn-sm btn-outline"
                        >
                          <Eye size={16} />
                          View Report
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {stats.totalAttempts === 0 && (
            <div className={styles.contentSection}>
              <div className={styles.emptyState}>
                <BookOpen size={64} />
                <h3>Start Your Learning Journey</h3>
                <p>
                  Complete assessments to track your progress and unlock
                  insights
                </p>
                <a href="/assessments" className="btn btn-primary">
                  <Target size={18} />
                  Browse Assessments
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Progress;
